import React from 'react';
import { Briefcase, TrendingUp, Users, Award, Globe, Zap, Building2, DollarSign, CheckCircle, ArrowRight, Star, Network, Handshake, Target, Sparkles, Trophy, Plane } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import PageTitle from '../hooks/PageTitle';
import { Link } from 'react-router-dom';

const Careers = () => {
  return (
    <div className="min-h-screen pt-20">
      <PageTitle title="Careers" />
      {/* Hero Section - EMOTIVO */}
      <section className="relative py-32 bg-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white/20 text-white text-sm px-4 py-2 backdrop-blur-sm">
              A Platform for High-Performing Lawyers
            </Badge>
            <h1 className="text-5xl md:text-7xl font-serif mb-6 leading-tight">
              Stop Working for Others.<br />Start Building Your Empire.
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-8">
              Join 50+ elite solicitors who've taken control with our revolutionary model. Build. Scale. Internationalise. Your practice, your clients, your income—finally yours.
            </p>
            <div className="flex justify-center">
              <Link to="/careers/apply">
                <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 px-10 py-7 text-lg font-semibold group">
                  Get in touch
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="py-6 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 text-center">
            <div>
              <div className="text-2xl font-serif text-gray-900">50+</div>
              <div className="text-sm text-gray-600">Elite Solicitors</div>
            </div>
            <div className="hidden md:block w-px h-8 bg-gray-300"></div>
            <div>
              <div className="text-2xl font-serif text-gray-900">£45M+</div>
              <div className="text-sm text-gray-600">Earned by Partners (2024)</div>
            </div>
            <div className="hidden md:block w-px h-8 bg-gray-300"></div>
            <div>
              <div className="text-2xl font-serif text-gray-900">192%</div>
              <div className="text-sm text-gray-600">Avg First-Year Increase</div>
            </div>
            <div className="hidden md:block w-px h-8 bg-gray-300"></div>
            <div>
              <div className="text-2xl font-serif text-gray-900">4.9/5</div>
              <div className="text-sm text-gray-600">Solicitor Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* The Math That Changes Everything */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">The Math That Changes Everything</h2>
            <p className="text-xl text-gray-600">See what you could really be earning</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
            {/* Traditional Firm */}
            <Card className="border-2 border-gray-300 shadow-lg relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge variant="secondary" className="bg-gray-600 text-white">Traditional Firm</Badge>
              </div>
              <CardContent className="p-10">
                <h3 className="text-2xl font-serif text-gray-900 mb-6 text-center">What You're Earning Now</h3>
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Fees You Generate</span>
                    <span className="font-semibold text-gray-900">£500,000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Your Salary</span>
                    <span className="font-semibold text-gray-900">£120,000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Firm Takes</span>
                    <span className="font-semibold text-red-600">£380,000</span>
                  </div>
                </div>
                <div className="bg-gray-100 rounded-lg p-6 text-center">
                  <div className="text-sm text-gray-600 mb-2">You Keep</div>
                  <div className="text-4xl font-serif text-gray-900">24%</div>
                </div>
              </CardContent>
            </Card>

            {/* Ashbourne Model */}
            <Card className="border-2 border-green-500 shadow-2xl relative bg-gradient-to-br from-green-50 to-white">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-green-600 text-white">Ashbourne Model 🚀</Badge>
              </div>
              <CardContent className="p-10">
                <h3 className="text-2xl font-serif text-gray-900 mb-6 text-center">What You Could Earn</h3>
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Fees You Generate</span>
                    <span className="font-semibold text-gray-900">£500,000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-green-200">
                    <span className="text-gray-600">You Take Home (70-90%)</span>
                    <span className="font-semibold text-green-600 text-xl">£350,000+</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Platform (30%)</span>
                    <span className="font-semibold text-gray-900">£150,000</span>
                  </div>
                </div>
                <div className="bg-green-100 rounded-lg p-6 text-center">
                  <div className="text-sm text-green-700 mb-2">You Keep</div>
                  <div className="text-4xl font-serif text-green-700">70-90%</div>
                  <div className="text-2xl font-semibold text-green-600 mt-4">+£230,000/year</div>
                  <div className="text-xs text-green-700 mt-1">minimum vs traditional firm</div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <p className="text-xl font-semibold text-gray-900 mb-2">That's an extra £230,000 in your pocket. Every single year.</p>
            <p className="text-gray-600">And it scales as you perform. Up to 90% + equity participation.</p>
          </div>
        </div>
      </section>

      {/* Progressive Fee Structure - DETTAGLIATO */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">Progressive, Performance-Driven Structure</h2>
            <p className="text-xl text-gray-600">Your share increases as you demonstrate performance and contribution</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            <Card className="border-2 border-gray-300 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-serif text-gray-900 mb-2">70%</div>
                <div className="text-sm font-semibold text-gray-700 mb-2">Entry Level</div>
                <p className="text-xs text-gray-600">Starting point for established lawyers</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-400 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-serif text-blue-600 mb-2">75-80%</div>
                <div className="text-sm font-semibold text-blue-700 mb-2">Established</div>
                <p className="text-xs text-gray-600">Proven track record</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-400 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-serif text-purple-600 mb-2">85%</div>
                <div className="text-sm font-semibold text-purple-700 mb-2">Top Performers</div>
                <p className="text-xs text-gray-600">Exceptional results</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-500 shadow-xl hover:shadow-2xl transition-shadow bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-serif text-green-600 mb-2">90%</div>
                <div className="text-sm font-semibold text-green-700 mb-2">Strategic Hires</div>
                <p className="text-xs text-gray-600">Exceptional talent</p>
                <Badge className="mt-2 bg-green-600 text-white text-xs">Premium</Badge>
              </CardContent>
            </Card>

            <Card className="border-2 border-yellow-500 shadow-xl hover:shadow-2xl transition-shadow bg-gradient-to-br from-yellow-50 to-white">
              <CardContent className="p-6 text-center">
                <Trophy className="h-10 w-10 text-yellow-600 mx-auto mb-2" />
                <div className="text-sm font-semibold text-yellow-700 mb-2">Partnership</div>
                <p className="text-xs text-gray-600">Equity participation</p>
                <Badge className="mt-2 bg-yellow-600 text-white text-xs">Ownership</Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Multiple Revenue Streams - DETTAGLIATO */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">Multiple Revenue Streams</h2>
            <p className="text-lg text-gray-600">Maximize your earnings through diverse opportunities</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Your Own Clients */}
            <Card className="border-2 border-green-500 shadow-xl">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-green-600 rounded-lg flex items-center justify-center mb-6">
                  <Briefcase className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-serif text-gray-900 mb-4">Your Own Clients</h3>
                <div className="mb-6">
                  <div className="text-4xl font-serif text-green-600 mb-2">70-90%</div>
                  <p className="text-sm text-gray-600">Based on your performance tier</p>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Your portable book of business</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Full control over client relationships</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>You own the client connection</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Internal Referrals */}
            <Card className="border-2 border-blue-500 shadow-xl">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-blue-600 rounded-lg flex items-center justify-center mb-6">
                  <Network className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-serif text-gray-900 mb-4">Internal Referrals</h3>
                <div className="mb-6 space-y-3">
                  <div>
                    <div className="text-2xl font-serif text-blue-600 mb-1">~10%</div>
                    <p className="text-xs text-gray-600">When you originate work</p>
                  </div>
                  <div>
                    <div className="text-2xl font-serif text-blue-600 mb-1">70-90%</div>
                    <p className="text-xs text-gray-600">When you execute work</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Structured internal economy</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Cross-practice collaboration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Passive income from referrals</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Firm-Generated Work */}
            <Card className="border-2 border-purple-500 shadow-xl">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-purple-600 rounded-lg flex items-center justify-center mb-6">
                  <Building2 className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-serif text-gray-900 mb-4">Firm-Generated</h3>
                <div className="mb-6">
                  <div className="text-4xl font-serif text-purple-600 mb-2">50-60%</div>
                  <p className="text-sm text-gray-600">Work sourced by Ashbourne</p>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Brand positioning initiatives</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Institutional relationships</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>International network deals</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="border-2 border-yellow-400 bg-yellow-50 max-w-3xl mx-auto">
            <CardContent className="p-6 text-center">
              <Sparkles className="h-8 w-8 text-yellow-600 mx-auto mb-3" />
              <p className="text-gray-900 font-semibold mb-2">Create Multiple Income Streams</p>
              <p className="text-sm text-gray-700">
                Combine your clients + internal referrals + firm-generated work to maximize earnings
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Ashbourne Beats Everyone */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif mb-4">Why Ashbourne vs Everyone Else</h2>
            <p className="text-xl text-gray-300">Traditional firms AND other consultant platforms</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-green-500 shadow-xl bg-gray-800 text-white">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <CheckCircle className="h-8 w-8 text-green-400 mr-3" />
                  <h3 className="text-2xl font-serif">At Ashbourne</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Keep 70-90% of what you earn",
                    "Structured internal deal flow",
                    "Active business development support",
                    "Personal brand development platform",
                    "International client network access",
                    "Collaborative internal ecosystem",
                    "Clear pathway to partnership & equity",
                    "No billing targets or timesheets"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-200">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-red-300 shadow-xl bg-gray-700">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-red-600 text-xl">✗</span>
                  </div>
                  <h3 className="text-2xl font-serif text-white">Others</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Keep 20-35% (traditional) or just autonomy (consultancy)",
                    "No structured deal flow",
                    "Limited BD support",
                    "No focus on personal brand",
                    "Restricted international access",
                    "Weak internal network",
                    "No meaningful equity participation",
                    "Rigid structures or isolated model"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <div className="h-5 w-5 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        <span className="text-red-600 text-xs">✗</span>
                      </div>
                      <span className="text-gray-300 line-through">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <p className="text-2xl font-serif mb-4">Not Just Independence. Growth, Scale & Value Creation.</p>
            <p className="text-gray-300">Ashbourne was designed to address the gaps in both traditional firms and consultant platforms</p>
          </div>
        </div>
      </section>

      {/* Complete Platform - Accordion */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">Beyond Fee-Sharing: The Complete Platform</h2>
            <p className="text-lg text-gray-600">What makes Ashbourne truly different</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="business-dev" className="border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                <div className="flex items-center">
                  <Target className="h-6 w-6 mr-3 text-blue-600" />
                  Business Development Support
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pt-4">
                <p className="mb-4">We actively support lawyers in expanding their client base:</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Assistance with client pitches and proposals</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Introductions to investors, corporates and professional networks</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Participation in business development initiatives</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Not limited to supporting existing practices—we help you grow revenue and market position</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="brand" className="border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                <div className="flex items-center">
                  <Sparkles className="h-6 w-6 mr-3 text-purple-600" />
                  Personal Brand & Market Positioning
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pt-4">
                <p className="mb-4">We support development of each lawyer's professional profile:</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Thought leadership initiatives and content development</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Participation in events and industry exposure</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Professional profile development and PR support</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Position yourself as a recognized expert in your field</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="team" className="border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                <div className="flex items-center">
                  <Users className="h-6 w-6 mr-3 text-green-600" />
                  Team Building & Practice Development
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pt-4">
                <p className="mb-4">Flexibility to build and scale your practice:</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Join individually or bring your entire team</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Develop and lead a practice area within the platform</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Build and scale your team over time</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Structured to support entrepreneurial lawyers in scaling their practice</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="partnership" className="border-2 border-yellow-400 bg-yellow-50 rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                <div className="flex items-center">
                  <Award className="h-6 w-6 mr-3 text-yellow-600" />
                  Partnership & Equity Opportunity
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pt-4">
                <p className="mb-4 font-semibold text-gray-900">A structured pathway to ownership:</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <Star className="h-4 w-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0 fill-current" />
                    <span>Lawyers who demonstrate consistent performance may be invited to become Partners</span>
                  </li>
                  <li className="flex items-start">
                    <Star className="h-4 w-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0 fill-current" />
                    <span>Selected individuals offered opportunity to participate in firm equity</span>
                  </li>
                  <li className="flex items-start">
                    <Star className="h-4 w-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0 fill-current" />
                    <span>Participation in firm profits beyond personal billings</span>
                  </li>
                  <li className="flex items-start">
                    <Star className="h-4 w-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0 fill-current" />
                    <span>Long-term value creation and alignment with firm's strategic development</span>
                  </li>
                  <li className="flex items-start">
                    <Star className="h-4 w-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0 fill-current" />
                    <span><strong>Ashbourne is designed not only as a place of work, but as a platform for building ownership</strong></span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="international" className="border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                <div className="flex items-center">
                  <Globe className="h-6 w-6 mr-3 text-indigo-600" />
                  International Platform & Network
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pt-4">
                <p className="mb-4">Transform your domestic practice into an international one:</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Offices in UK, Europe, Middle East (Dubai/Gulf), and planned US expansion</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Cross-border collaboration opportunities</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>International deal flow and client access</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Access to a broader international client base</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="performance" className="border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                <div className="flex items-center">
                  <Trophy className="h-6 w-6 mr-3 text-orange-600" />
                  Performance-Based Incentives
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pt-4">
                <p className="mb-4">Rewards beyond individual billing:</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Internal collaboration and referrals</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Client development</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Generation of international work</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Recruitment of new consultants</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Contribution to growth of the platform</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Elite Network & Events */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Plane className="h-16 w-16 mx-auto mb-6 text-gray-900" />
            <h2 className="text-4xl font-serif text-gray-900 mb-4">Elite Professional Network & Events</h2>
            <p className="text-lg text-gray-600">Connect with high-performing peers across jurisdictions</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Trophy className="h-10 w-10 text-yellow-600 mb-4" />
                <h3 className="text-xl font-serif text-gray-900 mb-3">International Retreats</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Annual events in leading European locations for top performers and partners
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Users className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="text-xl font-serif text-gray-900 mb-3">Partner-Level Events</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Exclusive networking with leadership and strategic discussions
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Globe className="h-10 w-10 text-green-600 mb-4" />
                <h3 className="text-xl font-serif text-gray-900 mb-3">Financial Centre Events</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Regular networking across London, Luxembourg, Dubai
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-serif text-gray-900 mb-6">
            Ready to 3X Your Income & Build Long-Term Value?
          </h2>
          <p className="text-xl text-gray-600 mb-4">
            Join 50+ elite lawyers. Average first-year increase: 192%
          </p>
          <p className="text-lg text-gray-500 mb-12">
            Not simply a place to work. A platform for growth, scale, and value creation.
          </p>
          
          <div className="flex justify-center mb-8">
            <a 
              href="https://drive.google.com/file/d/14bq-c0uPr1dYmhpvDbC9eXfHcPfMJh1a/view?usp=drivesdk"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-12 py-8 text-xl font-semibold">
                Download Full Prospectus
              </Button>
            </a>
          </div>

          <div className="flex items-center justify-center gap-8 text-sm text-gray-600 mb-12">
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
              <span>100% confidential</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
              <span>No obligations</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
              <span>Partnership track</span>
            </div>
          </div>

          <div className="p-8 bg-gray-50 border-2 border-gray-200 rounded-lg max-w-2xl mx-auto">
            <p className="text-gray-900 font-serif text-2xl mb-4 italic">
              "Ashbourne is not simply a place to work. It is a platform through which lawyers can build their practice, expand their network and create lasting professional value."
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;